import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Warning from "./Warning";
import { FormControlLabel } from "@material-ui/core";
import {
    DelegeeRevokeModel,
    RevokeProductModel,
} from "../../models/vehicle-assignments/RevokeEntryRightsModel";
import { revokeEntryRights, RevokeProductType } from "../../services/entryRights.service";
import useAppContext from "../../context/hooks/useAppContext";
import { useAppDispatch, useAppSelector } from "../../store";
import { toggleShowRevokeEntryRightsAction } from "../../store/actions/entryRights.actions";
import {
    EntryRightProductKey,
    selectDefaultRevocationEmailValue,
    isEntryRightsMyProductsRowKeyArray,
    selectEnableDelegationsScheduling,
    isEntryRightsMyProductsRowKey,
} from "../../containers/FleetManager/MyProducts/ParkingRightsDataTable/common";
import { groupBy } from "lodash";
import { useFleetManagerContext } from "../../context/hooks";
import { PedestrianRightsMyFleetState } from "../../models/delegations/DelegatedEntryRight";

export type RevokeEntryRightWarningProps = {
    revokeProductType: RevokeProductType;
};

function getRevokeModelFromMyProductKeys(rowKeys: EntryRightProductKey[]): DelegeeRevokeModel[] {
    const delegeeRevokeModels = Object.entries(groupBy(rowKeys, (rowKey) => rowKey.delegeeId)).map(
        ([delegeeId, entries]): DelegeeRevokeModel => {
            return {
                delegeeId,
                products: entries.flatMap(({ entryRightId, pmcId }) => {
                    if (!entryRightId || !pmcId) {
                        return [];
                    }

                    return [{ entryRightId, pmcId }];
                }),
            };
        }
    );
    return delegeeRevokeModels;
}

function getEntryRightIds(
    delegeeId: string,
    myFleet: PedestrianRightsMyFleetState
): RevokeProductModel[] {
    for (const pageNumber in myFleet.delegees.pages) {
        for (const delegee of myFleet.delegees.pages[pageNumber as any as number].data || []) {
            if (delegee.registrationId === delegeeId) {
                return delegee.products.map((product): RevokeProductModel => {
                    return {
                        entryRightId: product.parkingRightId,
                        pmcId: product.pmcId,
                    };
                });
            }
        }
    }

    return [];
}

function getDelegeeRevokeModesForMyFleet(
    selectedPedestrianIds: string[],
    myFleet: PedestrianRightsMyFleetState
): DelegeeRevokeModel[] {
    return selectedPedestrianIds
        .map((delegeeId: string) => {
            return {
                delegeeId,
                products: getEntryRightIds(delegeeId, myFleet),
            };
        })
        .filter((x) => x.products.length > 0);
}

const RevokeEntryRightWarning = ({ revokeProductType }: RevokeEntryRightWarningProps) => {
    const ns = revokeProductType === "parking" ? "revokeDelegeeWarning" : "revokePedestrianWarning";
    const { t } = useTranslation([ns, "globals"]);
    const { appState } = useAppContext();
    const dispatch = useAppDispatch();

    const loading = useAppSelector((state) => state.parkingProducts.loading || state.entryRights.loading);
    const myFleet = useAppSelector((state) => state.entryRights.myFleet);
    const defaultEmailValue = useAppSelector(selectDefaultRevocationEmailValue);
    const enableDelegationsScheduling = useAppSelector(selectEnableDelegationsScheduling);

    const [sendRevocationEmail, setSendRevocationEmail] = useState(defaultEmailValue);
    const { clearSelections, fleetManagerState } =
        useFleetManagerContext();
    const { selectedDelegeeRowKey, selectedDelegeeId, selectedRowKeys } = fleetManagerState;

    const delegeeRevokeModels = useMemo((): DelegeeRevokeModel[] => {
        if (isEntryRightsMyProductsRowKey(selectedDelegeeRowKey)) {
            return getRevokeModelFromMyProductKeys([selectedDelegeeRowKey]);
        }

        if (selectedRowKeys.length > 0) {
            if (isEntryRightsMyProductsRowKeyArray(selectedRowKeys)) {
                return getRevokeModelFromMyProductKeys(selectedRowKeys);
            }

            return getDelegeeRevokeModesForMyFleet(selectedRowKeys, myFleet);
        }

        if (selectedDelegeeId) {
            if (isEntryRightsMyProductsRowKey(selectedDelegeeId)) {
                return getRevokeModelFromMyProductKeys([selectedDelegeeId]);
            }

            return getDelegeeRevokeModesForMyFleet([selectedDelegeeId], myFleet);
        }

        return [];
    }, [myFleet, selectedRowKeys, selectedDelegeeRowKey, selectedDelegeeId, enableDelegationsScheduling]);

    const revokeCount = useMemo(
        () => delegeeRevokeModels.reduce((acc, model) => acc + model.products.length, 0),
        [delegeeRevokeModels]
    );

    const warningText = t("warning", { count: revokeCount });
    const warningDetails = t("details", { count: revokeCount });
    const toggleRevokePedestrianWarning = () => dispatch(toggleShowRevokeEntryRightsAction());

    const handleRevokeCancellation = () => {
        toggleRevokePedestrianWarning();
    };

    const handleSuccessfulRevoke = () => {
        toggleRevokePedestrianWarning();
        clearSelections();
    };

    const handleRevokeConfirmation = () => {
        if (delegeeRevokeModels.length === 0) {
            throw new Error("shouldn't happen");
        }

        return dispatch(
            revokeEntryRights(
                {
                    customerId: appState.user.seasonTicketOwnerCrmId!,
                    delegeeRevokeModels,
                    sendRevocationConfirmationEmail: sendRevocationEmail,
                },
                revokeProductType,
                handleSuccessfulRevoke
            )
        );
    };

    return (
        <Warning
            warning={warningText}
            details={warningDetails}
            onCancel={handleRevokeCancellation}
            onConfirm={handleRevokeConfirmation}
            disabled={false}
            confirmText={t("globals:revoke")}
            loading={loading}
        >
            <div>
                <FormControlLabel
                    control={
                        <qp-switch
                            data-size="s"
                            data-checked={sendRevocationEmail}
                            onClick={() => setSendRevocationEmail(!sendRevocationEmail)}
                        ></qp-switch>
                    }
                    label={t("deleteEntryRightDelegeesWarning:sendRevocationEmail")}
                />
            </div>
        </Warning>
    );
};

export default RevokeEntryRightWarning;
